/* This module sets default options for the DataTables.net 2.x plugin with
 * Bootstrap 5 integration. It provides a declarative HTML API via data-table-*
 * attributes, matching the pattern established by the c2Archive.dataTables module but
 * using the modern DataTables 2.x API conventions.
 *
 * Usage:
 *
 *   <!-- Auto-initialized on page load -->
 *   <table data-table data-table-sort="3,asc" data-table-source="/api/data/">
 *     ...
 *   </table>
 *
 *   <!-- Explicit initialization with custom options -->
 *   c2.dataTables.init('#my-table', { pageLength: 50 });
 *
 * Ported from: static_src/js/c2Archive/datatables.js
 */

(function (__module__, c2, $, _) {
  'use strict';

  var logger = c2.logging.getLogger(__module__);

  // Defensive fallbacks for Django i18n functions (loaded via javascript-catalog)
  var _gettext = typeof gettext === 'function' ? gettext : function (s) { return s; };
  var _interpolate = typeof interpolate === 'function' ? interpolate : function (s, args, named) {
    if (named) { for (var k in args) { s = s.replace('%(' + k + ')s', args[k]); } }
    return s;
  };

  // DateTime 1.x only attaches itself as DataTable.DateTime, but ColumnControl's
  // searchDateTime looks it up with DataTable.use('datetime') and falls back to
  // a plain text input without it
  if ($.fn.dataTable && $.fn.dataTable.DateTime) {
    $.fn.dataTable.use($.fn.dataTable.DateTime);

    // DateTime hides its picker on a body click outside it, but ColumnControl
    // stops propagation of clicks on its header buttons, so opening another
    // column's filter would leave the first filter's picker open. Track open
    // pickers and hide them from a capture-phase listener instead, which runs
    // before anything can stop the click.
    var dtProto = $.fn.dataTable.DateTime.prototype;
    var openPickers = [];
    var origShow = dtProto._show;
    var origHide = dtProto._hide;
    dtProto._show = function () {
      if (openPickers.indexOf(this) === -1) {
        openPickers.push(this);
      }
      return origShow.apply(this, arguments);
    };
    dtProto._hide = function () {
      _.pull(openPickers, this);
      return origHide.apply(this, arguments);
    };
    document.addEventListener('click', function (e) {
      _.forEach(openPickers.slice(), function (picker) {
        if (e.target !== picker.dom.input[0] &&
            !picker.dom.container[0].contains(e.target)) {
          picker.hide();
        }
      });
    }, true);
  }


  /* Set DataTable defaults for Bootstrap 5 styling.
  * DOM CHANGES - Allow the natural datatable dom in c2 version.
  * Removed potential hook points for code, so preserving reference here from c2Archive.datatables
  * <'panel panel-default'> + <'panel-body dataTables-header'> -> added 'card card-body' to dt-container
  * <'dataTables_toolbar'>"   -> replaced with <'dt-buttons'>
  *  <'scroll-wide-content'> -> added to 'dt-layout-table'
  * <'panel-body dataTables-footer'>
  */
  const dtDefaults = ($.fn.dataTable && $.fn.dataTable.defaults) || {};
  const defaults = _.merge({}, dtDefaults, {
    lengthMenu: [10, 25, 50, 100, 500],
    autoWidth: false,
    processing: true,
    pageLength: 25,
    searchDelay: 500,
    layout: {
      topStart: {
        buttons: [] // placeholder for addToolbar()
      },
      topEnd: 'search',
      bottomStart: ['pageLength', 'info'],
      bottomEnd: 'paging'
    },
    language: {
      info: _interpolate(
        _gettext('%(range)s of %(total)s items'),
        {
          'range': '<span class="dt-items-shown">_START_ - _END_</span>',
          'total': '<span class="dt-items-total">_TOTAL_</span>'
        },
        true),
      infoFiltered: _interpolate(_gettext(' (%(count)s total)'), { 'count': '_MAX_' }, true),
      infoEmpty: _gettext('No items'),
      lengthMenu: '_MENU_ &nbsp;',
      emptyTable: _gettext('No results found'),
      processing: '<div class="img-spinner" height="120" aria-hidden="true" />'
    },

    initComplete: function () {
      var $dt = this.api().table().node();
      var $table = $($dt);
      $table.addClass('table table-hover table-sm');
      $(this.api().table().container()).addClass('card card-body');
      $('.dt-layout-table').addClass('scroll-wide-content');
      $table.removeClass('d-none');
    },

    drawCallback: function (settings) {
      var $dt = $(settings.table);
      initStandardTableBehaviors($dt);
      var isClickable = (String($dt.data('table') || '').split(' ').indexOf('clickable') !== -1);
      isClickable && $dt.DataTable().isServerSide() && initClickableDrawBehavior($dt);
      // Broadcast the 'table:draw' event for custom handlers
      $dt.trigger('table:draw', settings);
    },

    // Save the state of tables in localStorage
    stateSave: true,
    stateSaveCallback: function (settings, data) {
      // Always restore a table to the first page, avoiding user confusion.
      data.start = 0;
      var key = keyForDataTable(settings.tableId || '');
      localStorage.setItem(key, JSON.stringify(data));
    },
    stateLoadCallback: function (settings) {
      var key = keyForDataTable(settings.tableId || '');
      try {
        return JSON.parse(localStorage.getItem(key));
      } catch (e) {
        console.log(e);
      }
    }
  });
  const simplifiedTableDefaults = {
    "paging": false,
    "searching": false,
    "info": false,
    "ordering": false,
    "layout": {
        "topStart": null,
        "topEnd": null,
        "bottomStart": null,
        "bottomEnd": null
    }
  };
  /* Convert DataTables 2.x server-side parameters to the legacy 1.x format
   * that the existing Django views expect.
   *
   * DT 2.x sends: { draw, start, length, search: {value, regex}, order: [{column, dir}], columns: [...] }
   * Django expects: sEcho, iDisplayStart, iDisplayLength, sSearch, iSortCol_0, sSortDir_0, etc.
   */
  function _buildLegacyParams(d) {
    var aoData = [
      { name: 'sEcho',          value: d.draw },
      { name: 'iDisplayStart',  value: d.start },
      { name: 'iDisplayLength', value: d.length },
      { name: 'sSearch',        value: d.search ? d.search.value : '' },
      { name: 'bRegex',         value: d.search ? d.search.regex : false },
      { name: 'iColumns',       value: d.columns ? d.columns.length : 0 },
      { name: 'iSortingCols',   value: d.order ? d.order.length : 0 }
    ];

    if (d.columns) {
      for (var i = 0; i < d.columns.length; i++) {
        var col = d.columns[i];
        aoData.push({ name: 'mDataProp_' + i,  value: col.data });
        aoData.push({ name: 'bSearchable_' + i, value: col.searchable });
        aoData.push({ name: 'bSortable_' + i,   value: col.orderable });
        aoData.push({ name: 'sSearch_' + i,      value: col.search ? col.search.value : '' });
        aoData.push({ name: 'bRegex_' + i,       value: col.search ? col.search.regex : false });

        // ColumnControl puts its per-column filters under col.columnControl
        // (see its preXhr handlers) rather than col.search, so they'd be lost
        // in the legacy conversion above. Forward them as flat params, read
        // server-side by utilities.cb_http.filter_queryset_from_column_control.
        var cc = col.columnControl;
        if (cc && cc.search) {
          aoData.push({ name: 'sCcSearch_' + i, value: cc.search.value });
          aoData.push({ name: 'sCcLogic_' + i,  value: cc.search.logic });
          aoData.push({ name: 'sCcType_' + i,   value: cc.search.type });
        }
        if (cc && cc.list) {
          _.forEach(cc.list, function (listValue) {
            aoData.push({ name: 'sCcList_' + i, value: listValue });
          });
        }
      }
    }

    if (d.order) {
      for (var j = 0; j < d.order.length; j++) {
        aoData.push({ name: 'iSortCol_' + j,  value: d.order[j].column });
        aoData.push({ name: 'sSortDir_' + j,   value: d.order[j].dir });
      }
    }

    return aoData;
  }


  var _ccLogicLabels = {
    contains: 'contains',
    notContains: 'does not contain',
    equal: '=',
    notEqual: '≠',
    starts: 'starts with',
    ends: 'ends with',
    greater: '>',
    greaterOrEqual: '≥',
    less: '<',
    lessOrEqual: '≤',
    empty: 'is empty',
    notEmpty: 'is not empty'
  };

  /* Return each column's ColumnControl filter in the shape its preXhr
   * handlers give server-side requests ({columns: [{columnControl: {search,
   * list} }] }), for client-side tables, which make no requests. ColumnControl
   * only exposes its current filters through state saving, so this collects
   * them from its stateSaveParams handlers into a throwaway state object,
   * which works whether or not stateSave is on.
   */
  function _columnControlDataFromState(dt) {
    var state = {};
    dt.trigger('stateSaveParams', [dt.settings()[0], state]);
    var ccState = state.columnControl || {};
    return {
      columns: _.map(dt.columns().indexes().toArray(), function (idx) {
        // Keyed as ColumnControl keys its state: by column name, or index
        var colState = ccState[dt.column(idx).name() || idx];
        if (!colState) {
          return {};
        }
        return { columnControl: { search: colState.searchInput, list: colState.searchList || [] } };
      })
    };
  }

  /* Return the chip label for a client-side searchList value: the visible
   * text of a cell in column `idx` whose search data is that value (which is
   * how ColumnControl builds the options on client-side tables), ignoring
   * .d-none content such as the hidden sort/search text in the Actions list's
   * Type cells.
   *
   * Finding the cell is a scan of every row and chips re-render on each draw,
   * so labels are cached per table until its data is reloaded. Misses aren't
   * cached, since the rows may not have loaded yet.
   */
  function _clientSideListLabel(dt, idx, value) {
    var settings = dt.settings()[0];
    if (!settings._ccListLabels) {
      settings._ccListLabels = {};
      dt.on('xhr.dt', function () {
        settings._ccListLabels = {};
      });
    }
    var key = idx + ':' + value;
    if (_.has(settings._ccListLabels, key)) {
      return settings._ccListLabels[key];
    }

    var rowIdx = _.find(dt.rows().indexes().toArray(), function (row) {
      var filter = dt.cell(row, idx).render('filter');
      return String(filter === null || filter === undefined ? '' : filter) === String(value);
    });
    if (rowIdx === undefined) {
      return value;
    }
    var $html = $('<div>').html(dt.cell(rowIdx, idx).render('display'));
    $html.find('.d-none').remove();
    return (settings._ccListLabels[key] = $html.text().replace(/\s+/g, ' ').trim());
  }

  /* Show the ColumnControl filters in effect as removable chips in a row
   * above the table. For server-side tables this is called from the ajax
   * wrapper in init(), by which point ColumnControl's preXhr handlers have
   * put each column's filter into `data` - so the chips always match what the
   * server is filtering on, including filters restored by stateSave on the
   * first load. Client-side tables call it on each draw, with `data` from
   * _columnControlDataFromState.
   *
   * Removing a chip clears that column's filter through ColumnControl's own
   * API (so its dropdown resets too) and redraws.
   */
  function _renderColumnControlChips(settings, data) {
    var dt = new $.fn.dataTable.Api(settings);
    var $container = $(dt.table().container());
    var $bar = $container.children('.dtcc-chips');

    if (!$bar.length) {
      var $tableRow = $container.children('.dt-layout-table').first();
      if (!$tableRow.length) {
        return;
      }
      // Same row/cell classes as the bootstrap5 DataTables layout renderer
      $bar = $('<div class="row mt-2 dtcc-chips d-none" role="group">' +
        '<div class="col-12 d-flex flex-wrap align-items-center gap-2"></div>' +
        '</div>');
      $bar.attr('aria-label', _gettext('Active column filters'));
      $tableRow.before($bar);

      // Re-render once the response arrives, so searchList values can show
      // the labels the server sends for them (e.g. "Active" for "ACTIVE").
      dt.on('xhr.dt', function () {
        _renderColumnControlChips(settings, $bar.data('ccRequestData'));
      });
      $bar.on('click', '.dtcc-chip .btn-close', function () {
        dt.column($(this).closest('.dtcc-chip').data('column')).columnControl.searchClear();
        dt.draw();
      });
      $bar.on('click', '.dtcc-chips-clear', function () {
        dt.columns().columnControl.searchClear();
        dt.draw();
      });
    }
    $bar.data('ccRequestData', data);

    var json = dt.ajax.json() || {};
    var listOptions = json.columnControl || {};
    var $cell = $bar.children().first().empty();
    var chipCount = 0;

    _.forEach((data && data.columns) || [], function (col, idx) {
      var cc = col.columnControl;
      if (!cc) {
        return;
      }
      var description = null;
      var search = cc.search;
      if (search && (search.value !== '' || search.logic === 'empty' || search.logic === 'notEmpty')) {
        description = _ccLogicLabels[search.logic] || search.logic;
        if (search.type === 'date') {
          description = { greater: 'after', less: 'before' }[search.logic] || description;
        }
        if (search.logic !== 'empty' && search.logic !== 'notEmpty') {
          description += ' ' + search.value;
        }
      }
      var listValues = _.values(cc.list || {});
      if (listValues.length) {
        var options = listOptions[idx] || [];
        description = _.map(listValues, function (value) {
          var option = _.find(options, function (o) { return String(o.value) === String(value); });
          var label = option ? option.label : value;
          if (!option && !dt.page.info().serverSide) {
            label = _clientSideListLabel(dt, idx, value);
          }
          // An empty label is the "Empty" option; name it as ColumnControl does
          return label === '' ? dt.i18n('columnControl.list.empty', 'Empty') : label;
        }).join(', ');
      }
      if (description === null) {
        return;
      }

      var title = $('<div>').html(dt.column(idx).title()).text().trim();
      var $chip = $('<span class="dtcc-chip badge rounded-pill text-bg-light border d-inline-flex align-items-center gap-1"></span>')
        .attr('data-column', idx)
        .append($('<span class="fw-semibold"></span>').text(title + ':'))
        .append($('<span></span>').text(description))
        .append($('<button type="button" class="btn-close"></button>')
          .attr('aria-label', _interpolate(
            _gettext('Remove filter: %(filter)s'), { filter: title + ' ' + description }, true
          )));
      $cell.append($chip);
      chipCount++;
    });

    if (chipCount > 1) {
      $cell.append($('<button type="button" class="dtcc-chips-clear btn btn-link btn-sm p-0"></button>')
        .text(_gettext('Clear all')));
    }
    $bar.toggleClass('d-none', chipCount === 0);
  }


  /* Initialize any tables having attribute 'data-table' as DataTables.
   * DataTable options may be specified through the data-table-* API.
   *
   * Args:
   *   selector - optional selector of table(s) to initialize
   *   options - a DataTables options object (optional)
   *
   * The data-table attribute may be empty or specify a predefined "type":
   * - 'formset': disables features that don't work well with formsets.
   *
   * Other data attributes can be used to configure the DataTable:
   *   - data-table-no-auto-init: prevents auto-initialization (when `init` is
   *     called without a specific selector, typically at document load).
   *   - data-table-options: inline DataTables options object
   *   - data-table-sort="3,asc": initial sort by col index 3 in asc order.
   *   - data-table-sort-disabled="0,9": disables sorting on specific columns.
   *   - data-table-source="/url/": enables server-side processing via that URL.
   *   - data-table-searchable="0,1": adds a ColumnControl search dropdown to
   *     specific columns.
   *   - data-table-search-list="2": adds a ColumnControl dropdown of known
   *     values to specific columns.
   *   - data-table-search-date="4": adds a ColumnControl date search, with a
   *     DateTime picker, to specific columns.
   *   - data-table-search-number="5": adds a ColumnControl number search to
   *     specific columns.
   */
  function init(selector, options) {
    var $table, type, types = [];
    var selectAllTables = 'table[data-table]';
    options = options || {};

    $(selector || selectAllTables).each(function () {
      $table = $(this);

      // Several downstream behaviors (batch checkboxes, sparklines, tooltips,
      // toggles) key off '#' + this id to re-select the table later. Without
      // an id, that becomes the literal selector '#undefined', which matches
      // nothing and breaks those behaviors (e.g. dt.isSingleSelect() throwing
      // on an empty API context in updateBatchCheckboxes).
      if (!$table.attr('id')) {
        $table.attr('id', _.uniqueId('dt-table-'));
      }

      // Options may be set as a data attribute on the table
      var inlineOptions = $table.data('table-options');
      var mergedOptions = _.merge({}, options, inlineOptions || {});

      // Avoid re-initializing any DataTables in this collection. Also skip
      // those that do not want to be *auto* initialized, i.e. when init is
      // called without a selector.
      if ($.fn.dataTable.isDataTable(this) ||
          ($table.data('table-no-auto-init') !== undefined && !selector)) {
        return;
      }

      type = $table.data('table');
      if (type) {
        types = String(type).split(' ');
      }

      // Combine various option sources into one object
      var typeOptions = {};
      if (types.indexOf('formset') !== -1) {
        typeOptions = _optionsForFormsetTable();
      }
      if (types.indexOf('clickable') !== -1) {
        typeOptions = _optionsForClickableTable();
      }
      if (types.indexOf('radio') !== -1) {
        typeOptions = _.merge({},  _optionsForClickableTable(), _optionsForRadioTable());
      }
      if (types.indexOf('scrollable') !== -1) {
        typeOptions = _.merge({}, typeOptions, _optionsForScrollableTable());
      }
      if (types.indexOf('dialog') !== -1) {
        typeOptions = _.merge({}, typeOptions, { stateSave: false });
      }

      // Order is important here, the first objects have the highest precedence. The defaults are last so that they can be overridden by any of the previous options.
      // so specified sort options are preferred over defaults based on the type (typeOptions)
      mergedOptions = _.defaults(
        mergedOptions,
        _optionsForSort($table.data('table-sort')),
        typeOptions,
        _optionsForDataSource($table.data('table-source'), $table.data('table-source-prop')),
        defaults,
        {searchCols: []} // Ensure searchCols resets to avoid error `TypeError: Cannot read properties of undefined (reading 'searchFixed')` resulting from length mismatch
      );

      // For server-side tables, wrap the ajax URL in a function that converts
      // DT 2.x parameters to the legacy format Django views expect, and calls
      // any fnServerParams callback set via data-table-fnServerParams.
      //
      // ColumnControl redraws on every keystroke, checkbox click and chip
      // removal, and DataTables' searchDelay only covers the global search
      // box. So requests where a ColumnControl filter changed are debounced
      // by searchDelay; a newer draw (e.g. paging) cancels a pending one and
      // is sent right away, since its `data` already has the latest filters.
      // DataTables ignores responses to superseded draws, so those are never
      // answered and any still in flight is aborted.
      if (mergedOptions.serverSide && typeof mergedOptions.ajax === 'string') {
        var url = mergedOptions.ajax;
        var serverParams = $table.data('table-fnServerParams');
        var searchDelay = mergedOptions.searchDelay;
        var pendingRequest = null;
        var inFlightRequest = null;
        var lastColumnControlState;
        mergedOptions.ajax = function (data, callback, settings) {
          // Chips update immediately, even when the request is debounced
          _renderColumnControlChips(settings, data);

          var columnControlState = JSON.stringify(_.map(data.columns, 'columnControl'));
          var filterChanged = lastColumnControlState !== undefined &&
            columnControlState !== lastColumnControlState;
          lastColumnControlState = columnControlState;

          clearTimeout(pendingRequest);
          // Tracked here rather than via settings.jqXHR, which DataTables
          // overwrites with this function's return value
          var send = function () {
            if (inFlightRequest && inFlightRequest.readyState !== 4) {
              inFlightRequest.abort();
            }
            var aoData = _buildLegacyParams(data);
            if (typeof serverParams === 'function') {
              serverParams(aoData);
            }
            inFlightRequest = settings.jqXHR = $.ajax({
              url: url,
              data: aoData,
              dataType: 'json',
              cache: false,
              type: settings.serverMethod || 'GET',
              success: callback
            });
            return inFlightRequest;
          };
          if (filterChanged && searchDelay) {
            pendingRequest = setTimeout(send, searchDelay);
            return undefined;
          }
          return send();
        };
        $table.one('destroy.dt', function () {
          clearTimeout(pendingRequest);
        });
      }

      // Column defs need special handling to *extend* existing defs
      var sortDisabled = $table.data('table-sort-disabled');
      var sortDisabledColumns = [];
      if (sortDisabled !== undefined) {
        sortDisabledColumns = _.map(String(sortDisabled).split(','), Number);
        var newDef = { orderable: false, targets: sortDisabledColumns };
        if (mergedOptions.columnDefs) {
          mergedOptions.columnDefs.push(newDef);
        } else {
          mergedOptions.columnDefs = [newDef];
        }
      }

      // Per-column ColumnControl dropdowns (sort options plus a search).
      // Server-side views must apply these filters themselves, e.g. with
      // utilities.cb_http.filter_queryset_from_column_control.
      //   - data-table-searchable="0,1,3": free-text search input; 'search'
      //     auto-picks a text/number/date input based on the column type.
      //   - data-table-search-list="4,5": dropdown of known values
      //     (ColumnControl's searchList). On serverSide tables the values must
      //     come from a `columnControl` block of the Ajax JSON response, keyed
      //     by column index, or the dropdown renders empty - see
      //     https://datatables.net/ref/core/content/searchList
      //   - data-table-search-date="4": date search with a DateTime picker.
      //     'search' would pick this itself only for a column whose cells
      //     DataTables detects as dates, which rendered cells like "3 days
      //     ago" are not. Dates are sent to the server as YYYY-MM-DD. The
      //     dropdown doesn't autofocus its input, since focusing it pops the
      //     picker open over the dropdown.
      //   - data-table-search-number="5": number search, for columns whose
      //     rendered cells (e.g. with tooltip HTML) 'search' wouldn't detect
      //     as numbers.
      // A column listed more than once gets the def that comes first:
      // DataTables applies columnDefs in reverse, so earlier ones win. That
      // also keeps these defs from overriding a table type's own (e.g. the
      // clickable checkbox column's), which are already in columnDefs.
      var hasColumnControl = false;
      _.forEach([
        ['table-searchable', 'search'],
        ['table-search-number', 'searchNumber'],
        ['table-search-date', { extend: 'searchDateTime', format: 'YYYY-MM-DD' }, { autoFocus: '' }],
        ['table-search-list', 'searchList']
      ], function (triple) {
        var cols = $table.data(triple[0]);
        if (cols === undefined || cols === '') {
          return;
        }
        // No sort options on tables with ordering disabled
        // (data-table-sort="disabled") or on columns in
        // data-table-sort-disabled. ColumnControl's sort buttons call
        // dt.order(), which ignores `orderable: false`, so they'd send a sort
        // the server doesn't support for that column.
        var targets = _.map(String(cols).split(','), Number);
        var unsortable = mergedOptions.ordering === false
          ? targets
          : _.intersection(targets, sortDisabledColumns);
        _.forEach([
          [_.difference(targets, unsortable), ['orderAsc', 'orderDesc', 'spacer', triple[1]]],
          [unsortable, [triple[1]]]
        ], function (group) {
          if (!group[0].length) {
            return;
          }
          var ccDef = {
            columnControl: [_.assign({ extend: 'dropdown', content: group[1] }, triple[2])],
            targets: group[0]
          };
          if (mergedOptions.columnDefs) {
            mergedOptions.columnDefs.push(ccDef);
          } else {
            mergedOptions.columnDefs = [ccDef];
          }
        });
        hasColumnControl = true;
      });

      logger.debug('  table options: ', mergedOptions);
      var dt = $table.DataTable(mergedOptions);

      // Filter chips for client-side tables (server-side ones render them
      // from the ajax wrapper above). The first draw happens during init,
      // before this listener exists, so render once now too.
      if (hasColumnControl && !mergedOptions.serverSide) {
        var renderChips = function () {
          _renderColumnControlChips(dt.settings()[0], _columnControlDataFromState(dt));
        };
        dt.on('draw.dt', renderChips);
        renderChips();
      }
      addToolbar($table);
      addRefreshButton($table);

      $table.show()
      // Now that the table is initialized, hook up the clickable plugin to
      // reflect any existing selections. This is the case in the Env > Groups
      // > Add dialog, where the table is initialized with related groups selected.
      if (-1 != types.indexOf('clickable')) {
        setupClickableTable($table);
        c2.dialogs.onSubmitSerializeAllSelectedRows($table);
      }
    });
  }


  // Formsets currently don't work well with these DataTable features
  function _optionsForFormsetTable() {
    return {
      searching: false,
      ordering: false,
      stateSave: false,
      paging: false,
      info: false
    };
  }

  // For tables with selectable rows, check/selects inputs
  // For checkbox inputs, adds a tally of selected rows at the top of the table
  function setupClickableTable($table) {
    var dt = $table.DataTable();
    $table
      .on('select.dt', function(e, dt, type, indexes) {
        if (type !== 'row') { return; }
        dt.rows(indexes).nodes().to$().find('input.selector:checkbox,input.selector:radio').prop('checked', true);
        dt.saveSelectedRows();
        dt.fnUpdateSelectionInfo();
        c2.forms.updateBatchCheckboxes($table);
      })
      .on('deselect.dt', function(e, dt, type, indexes) {
        if (type !== 'row') { return; }
        dt.rows(indexes).nodes().to$()
          .find('input.selector:checkbox,input.selector:radio').prop('checked', false);
        dt.saveSelectedRows();
        dt.fnUpdateSelectionInfo();
        c2.forms.updateBatchCheckboxes($table);
      });
    // Clicking on tag spans should not select a row
    $table.on('user-select', function (e, dt, type, cell, originalEvent) {
      if ($(originalEvent.target).is('span.cb-tag')) {
          e.preventDefault();
      }
    });

    // Clicks on links/buttons inside table rows must not trigger row selection.
    // DT Select's click handler lives on div.dt-container (above the table in
    // the DOM), so stopping propagation here prevents it from ever firing.
    // We manually call the dialog handler so open-dialog links still work.
    $table.on('click', 'a[href]', function(e) {
      e.stopPropagation();
      if ($(this).hasClass('open-dialog')) {
        c2.dialogs.clickHandler(e);
      }
    });

    $table.on('click', '.cb-tag', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var $tag = $(e.target);
        $table.closest('.dt-container')
            .find('.dt-search input')
            .val('label: ' + $tag.text().trim())
            .trigger('keyup');
    });
    // Sync pre-checked checkboxes → DataTables selection (e.g. dialog opens with rows pre-selected from DB)
    dt.rows().every(function() {
      if ($(this.node()).find('input.selector:checkbox').prop('checked')) {
        this.select();
      }
    });
    dt.fnUpdateSelectionInfo();
    // Missing registered callbacks, see c2Archive.datatables.setupClickableTable()
    // Calls `_.forEach($table.data('on-selection-change')`
  }


  // Table presented in a fixed-height container with scrollable content.
  // Used by CheckboxSelectMultipleTable widgets in dialog forms.
  function _optionsForScrollableTable() {
    return {
      info: false,
      paging: false,
      scrollY: '250px',
      scrollCollapse: false
    };
  }

  // Tables where first column has checkbox and rows are clickable.
  // The checkbox column contains actual <input class="selector"> elements;
  // Select extension handles click-to-select and setupClickableTable syncs
  // the checkbox state via select.dt / deselect.dt events.
  function _optionsForClickableTable() {
    return {
      columnDefs: [{
        orderable: false,
        // The checkbox column has nothing to order/search by, and its header
        // cell is also where Select injects its own "select all" checkbox -
        // adding a columnControl row there (e.g. from a table-wide
        // columnControl option) conflicts with that
        // and breaks Select's setup (ctx._select never gets populated).
        // target 0 is the first header row (ColumnControl's default). This
        // def sits ahead of the data-table-searchable etc. defs init() appends,
        // and the first columnDef for a column wins, so those can't undo it.
        columnControl: { target: 0, content: [] },
        targets: 0
      }],
      select: {
        style: 'multi'
      },
      order: [[1, 'asc']]
    };
  }

  function _optionsForRadioTable() {
    return {
      select: {
        style: 'single'
      },
    };
  }

  /* Return a DataTable options object for initial sorting.
   * - sort="0"         -> sort by column 0 in ascending order
   * - sort="0,desc"    -> sort by column 0 in descending order
   * - sort="disabled"  -> disable sorting completely
   */
  function _optionsForSort(sort) {
    if (sort === undefined) {
      return { order: [] };
    }
    if (sort === 'disabled') {
      return { ordering: false };
    }

    sort = String(sort).split(',');
    var column = parseInt(sort[0], 10);
    var direction = sort[1] || 'asc';
    return { order: [[column, direction]] };
  }


  /* Return a DataTable options object for data source definition.
   *
   * Server-side URL:
   *   data-table-source="/some/url/to/jsondata"
   *
   * JSON array (inline or object):
   *   data-table-source="[{...}, {...}]"
   *   $table.data('table-source', [{...}, {...}])
   */
  function _optionsForDataSource(dataSource, dataSourceProp) {
    if (dataSource === undefined) {
      return {};
    }

    switch (typeof dataSource) {
      case 'string':
        dataSource = dataSource.trim();
        if (dataSource.charAt(0) === '[') {
          return { data: JSON.parse(dataSource) };
        }
        // It's a URL — enable server-side processing with legacy ajax params
        return {
          serverSide: true,
          ajax: dataSource
        };

      case 'object':
        const columns = _optionsForDataSourceProp(dataSourceProp);
        return {
          data: dataSource,
          ...columns && { columns }
         };

      default:
        return {};
    }
  }

  /* Return a dataTable columns object for property data selector to use when reading
   * the local source stored in $table.data('table-source')
   */
  function _optionsForDataSourceProp(props) {
    if (props !== undefined) {
      return _.map(props.split(','), function(prop) {
        return {'data': prop};
      })
    } return undefined
  }

  function keyForDataTable(tableId) {
    return 'DataTable#' + tableId + window.location.pathname;
  }

/*
* Set up standard behaviors found in many CB tables.  This is called in the draw handler before the trigger of the custom table:draw event
*
* Args:  $table: the DataTable object
*/
function initStandardTableBehaviors($table) {
  var tableId = '#' + $table.attr('id');
  if (c2.sparklines && c2.sparklines.init) {
    c2.sparklines.init(tableId + ' .sparkline');
  }

  // Initialize Bootstrap 5 tooltips on newly drawn rows
  if (c2.tooltips && c2.tooltips.init) {
    c2.tooltips.init(tableId);
  }

  // Enable 'select all/none' behavior on checkboxes for batch selection of rows or columns, if any exist in the table.
  c2.forms.enableBatchCheckboxes(tableId);

  // Create toggles and hook up their click behavior
  c2.toggles.enablePostOnChange(tableId + ' input[data-post-url]');
}

// Restoring saved row selections, necessary for server-side tables
function initClickableDrawBehavior($table) {
  const $dt = $table.DataTable();
  $dt.restoreSelectedRows();
}

/*
* Start Filter Functions
* Copied mostly unchanged from c2Archive.datatables
* Load the list filters panel via AJAX and set up the behavior of the buttons.
* Being used by the Server List Datatable, the admin/history Datatable, and at least one UIX in the cloudbolt-forge.
*/

function initListFilters(tableID, url) {
  /**
   * Controls the filters panel and initializes filters.
   * Controls the csv button, gets csv data, and initializes download.
   * Controls the hide/show filters panel toggle.
   * The url parameter is optional, but is useful if your filter form view has a different URL than just the
   * relative `/filter_form/`. This is the case for UI Extensions that use filters.
   *
   * **/
  var filtersLoaded = false;
  var $table = $(tableID);
  var $filtersPanel = $('#filters-panel');
  var $filtersPanelToggle = $('.filters-panel-toggle-btn-text');
  var $filterButton = $('#filters-panel-toggle');
  var $csvExportButton = $('#export-to-csv');
  // if the url is not specified, default to the relative "filter_form/" (this is what is used for the pages built into
  // the product such as the server list page and admin>history page)
  url = url || 'filter_form/';

  $filterButton.on('keydown', function(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      $(this).click();
    }
  });
  $filterButton.click(function() {
    if (filtersLoaded) {
      // only load the filters form once per loading of this page.  Ie. if
      // the user expands, collapses, and expands the filters, only load them
      // on the first expand click
      return;
    }
    c2.spinners.showSpinner({selector: '#filters-panel', fade: true})
    $filtersPanel.load(url, function() {
      c2.spinners.hideSpinner({selector: '#filters-panel', fade: true})
      filtersLoaded = true;

      // register the submit handler and click handler for the reset button
      // only after the server filter form is loaded
      $filtersPanel.find('form').submit(function(e) {
        e.preventDefault();

        $table.DataTable().clearSelection();

        // add a flag so server knows to persist new filter values
        $(this).append('<input type="hidden" name="new_filters" value="1" />');

        $table.DataTable().ajax.reload();
      });

      $('#unfilter').on('click', clearFilters);
    });
  });

  // control the CSV export button on click
  $csvExportButton.click(function() {
    var data = $table.DataTable().ajax.params();
    data.as_csv = 'true';
    var url = $table.attr('data-table-source');

    // get data to export and initialize download of csv file if the response is to export, else log errors
    $.get(url, data, function(response) {
      if (response.export) {
        c2.downloads.startDownloadFromContent(response.export, response.filename);
      } else if (response.errors) {
        _.forEach(response.errors, function(errors, index) {
          console.log('index: ', index);
          console.log('errors: ', errors);
        });
      }
    });

  });

  // toggle the filters button between 'Hide' and 'Show' display
  $filtersPanel.on('shown.bs.collapse', function () {
    $filtersPanelToggle.html(_gettext('Hide filters'));
  });
  $filtersPanel.on('hidden.bs.collapse', function () {
    $filtersPanelToggle.html(_gettext('Show Filters'));
  });
}

  // Serialize the list filter form and include with each dataTable request.
  // Also save all selected rows so they can be restored when the page redraws.
  function addListFilters(aoData) {
    var filterFields = $('#filters-panel form').serializeArray();
    var payload = {};
    $.each(filterFields, function(index, field) {
      // group form field objects by field name.
      if (!(field.name in payload)) {
          // make new
          payload[field.name] = [field.value];
      } else {
          // add to existing
          payload[field.name].push(field.value);
      }
    });

    aoData.push({name: 'filters_selected', value: JSON.stringify(payload)});
    aoData.push({name: 'with_checkboxes', value: 1});
    aoData.push({name: 'load_filters_from_profile', value: 1});
  }

// Callback invoked with server table JSON response which includes a count of the number of filters in use.
function updateFiltersInUseFromResponse(serverListResponse) {
  updateFiltersInUseMsg(serverListResponse?.json?.filtersInUse || 0);
}

function updateFiltersInUseMsg(qty) {
  // updates message on the Show/Hide Filters toggle button with the number of filters in use
  if (!qty || qty === 0) {
    $('#filtersInUse').html('');
  } else {
    $('#filtersInUse').html('<b>('+ qty + ' ' + _gettext('in use') + ')</b>');
  }
}

function clearFilters(e) {
  var $clearButton = $(this);
  var $field;

  // Clear all selectize widgets
  $('#filters-panel select').each(function() {
    $field = $(this)[0].selectize;
    $field.clear();
  });

  // uncheck all checkboxes to reset to no filters
  $('#filters-panel input:checkbox').prop("checked", false);

  $('#filters-panel input[type=text]').val('');
  // Save the cleared state server side
  $clearButton.submit();
  // Update Filter Count Message to Zero
  updateFiltersInUseMsg(0);
}
/*
* End Filter functions
*/


/* Handle Toolbars
* Pairs with src/templates/v5/common/datatable/datatable-selection-info-dropdown.html
* Clicks on toolbar buttons are handled as follows:
* - If the toolbar button specifies `data-href`, that URL will be used to load
*   the dialog. Otherwise, a common URL that handles multiple toolbar
*   actions can be passed to this function, in `commonUrl`. Each button must
*   then specify a `data-action` to let the view know which action is being
*   performed.
* - HTTP GET parameters are prepared (as an object) to represent selected
*   rows and action.  The param name is determined by the `name` attribute
*   of the checkboxes in the first column. E.g. input name="profile_id" will
*   be available to the dialog view as request.GET.getlist('profile_id[]',
*   []).
*
* A click handler for the selection info area is also set up for selecting
* no rows and all visible rows.
*/
function setupToolbarBehavior($table, $toolbar, commonUrl) {
  // Handle toolbar button/dropdown clicks
  $toolbar.on('click',
    '.btn.selection-action:not(.disabled):not(.dropdown-toggle),' +
    '.btn.selection-action:not(.disabled) + .dropdown-menu a',
    function(e) {
      e.preventDefault();

      var data = {};
      var dt = $table.DataTable();
      var fieldName = $table.find('input.selector').first().attr('name');
      var values = dt.rows({ selected: true }).nodes().to$()
        .find('input.selector').map(function() { return $(this).val(); }).get();
      // Merge in saved rows from saveSelectedRows() to avoid losing selections on server-side tables
      var savedValues =  dt.settings()[0].oLoadedState?.customRows || [];
      values = _.uniq(savedValues.concat(values));
      data[fieldName] = values;

      // The action button may specify data-href and data-action
      var $btn = $(this);
      var url = $btn.data('href') || commonUrl;
      var action = $btn.data('action');
      if (action) {
        data.action = action;
      }

      if ($btn.hasClass('selection-action-new-tab')) {
        // special case: don't open a dialog, just temporarily open a new tab that
        // can be used to do something like get and return a zip file
        var queryString = $.param(data);
        window.open(url + "?" + queryString, "_blank");
      } else {
        // default behavior: open a dialog
        var $dialog = $("#dialog-modal");

        // copy data attributes from the trigger to the dialog element to honor
        // data- API. This is how the conventional open-dialog handler
        // works, which we bypass here because we're calling get manually.
        $dialog.data($btn.data());

        // load the confirmation dialog via GET
        c2.dialogs.displayJqXHR(
          $.get(url, data)
        );
      }
    }
  );

  // Handle clicks on the selection info dropdown (select none/all)
  $toolbar.on('click', '.selection-info .dropdown-menu a', function(e) {
      var dt = $table.DataTable();
      var action = $(this).data('action');

      switch (action) {
        case 'select-none':
          dt.clearSelection();
          break;
        case 'select-page':
          dt.rows({ page: 'current' }).select();
          break;
        case 'select-all':
          console.log('selecting all rows in all pages is not supported yet');
          // specifically this will not work with the below code on a server-side table
          dt.rows().select();
          break;
      }
      c2.forms.updateBatchCheckboxes($table);
    }
  );
}


  /* If table has a toolbar defined in prop 'data-table-toolbar', append it to
   * the .dt-buttons area of this DataTable.
   *
   * The value of the data-table-toolbar attribute should be a selector of a
   * DOM fragment, or the fragment itself, with any behaviors already defined.
   */
  function addToolbar($table) {
    var toolbarSelector = $table.data('table-toolbar');
    if (toolbarSelector === undefined) {
      return;
    }

    var $toolbar = $(toolbarSelector);
    if ($toolbar && $toolbar.length) {
      var $dtWrapper = $table.closest('.dt-container');
      var $toolbarContainer = $dtWrapper.find('.dt-buttons');
      $toolbarContainer.append($toolbar);
      $toolbar.show();
    }
  }

  /* If table has 'data-table-refresh-btn' attribute, append a refresh button
   * to the .dt-buttons area. Clicking it calls ajax.reload(); the button is
   * disabled while DataTables is processing (fetching data).
   *
   * Tables populated by c2.history.loadHistoryData() (e.g. #history) hold
   * their data as a local JS array rather than a DataTables `ajax` source
   * (see c2/history.js for why), so DataTable().ajax.reload() has nothing to
   * reload and throws. Those tables are recognizable by the
   * 'table-source-data-url' data attribute that loadHistoryData() sets;
   * refresh them by calling loadHistoryData() again instead.
   */
  function addRefreshButton($table) {
    if ($table.data('table-refresh-btn') === undefined) {
      return;
    }

    var $dtWrapper = $table.closest('.dt-container');
    var $toolbarContainer = $dtWrapper.find('.dt-buttons');

    var $btn = $(
      '<button type="button" class="btn btn-sm btn-outline-secondary dt-refresh-btn" title="' +
      _gettext('Refresh') + '">' +
      '<span class="bi bi-arrow-repeat" aria-hidden="true"></span>' + _gettext('Refresh') +
      '</button>'
    );

    $btn.on('click', function () {
      if ($table.data('table-source-data-url') !== undefined) {
        c2.history.loadHistoryData();
      } else {
        $table.DataTable().ajax.reload();
      }
    });

    $table.on('processing.dt', function (e, settings, processing) {
      $btn.prop('disabled', processing);
    });

    $toolbarContainer.append($btn);
  }

  /*
   * Convenience function to reload DataTables that have a server-side source.
   */
  function reloadTable(selector) {
    try {
      $(selector).DataTable().ajax.reload();
    } catch (e) {
      console.error('Failed to reload DataTable "' + selector + '". Exception: ', e);
    }
  }


  /*
   * Insert visual group-header rows into a DataTable grouped by a column value,
   * then hide that grouping column.  Ported from c2Archive.dataTables.drawRowsGroupedByCol.
   */
  function drawRowsGroupedByCol(oTable, oSettings, groupingCol, headerClass) {
    if (oSettings.display.length === 0) {
      return;
    }
    var $rows = oTable.$('tbody tr');
    var colspan = $rows[0].getElementsByTagName('td').length;
    var lastGroup = '';

    for (var i = 0; i < $rows.length; i++) {
      var iDisplayIndex = oSettings.displayStart + i;
      var group = oSettings.data[oSettings.display[iDisplayIndex]].data[groupingCol];
      if (group !== lastGroup) {
        var nGroup = document.createElement('tr');
        var nCell = document.createElement('td');
        nCell.colSpan = colspan;
        nCell.className = (headerClass === undefined) ? 'grouped-by' : headerClass;
        nCell.innerHTML = group;
        nGroup.appendChild(nCell);
        $rows[i].parentNode.insertBefore(nGroup, $rows[i]);
        lastGroup = group;
      }
    }
    oTable.column(groupingCol).visible(false);
  }

  // Export module
  c2[__module__] = {
    init: init,
    defaults: defaults,
    simplifiedTableDefaults: simplifiedTableDefaults,
    reloadTable: reloadTable,
    initStandardTableBehaviors:initStandardTableBehaviors,
    initClickableDrawBehavior:initClickableDrawBehavior,
    setupToolbarBehavior: setupToolbarBehavior,
    // server and history list filters:
    initListFilters: initListFilters,
    addListFilters: addListFilters,
    updateFiltersInUseFromResponse: updateFiltersInUseFromResponse,
    drawRowsGroupedByCol: drawRowsGroupedByCol,
  };

})('dataTables', window.c2, window.jQuery, window._);
